import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  Loader2,
  Lock,
  Mail,
  Minus,
  Phone,
  Plus,
  ShoppingBag,
  Store,
  Truck,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { formatUsd } from '@/lib/format';
import {
  SUPPORT_MAILTO_HREF,
  SUPPORT_PHONE_TOLL_FREE,
  SUPPORT_TOLL_FREE_HREF,
} from '@/lib/constants';
import { useCatalog } from '@/hooks/useCatalog';
import { useCustomerPricing, priceFor, pricingPending } from '@/hooks/useCustomerPricing';
import { useCart } from '@/hooks/useCart';
import { useCustomer } from '@/hooks/useCustomer';
import { useSubmitOrder, type SubmitOrderResult } from '@/hooks/useSubmitOrder';
import { useActingAs } from '@/hooks/useActingAs';
import { useIsSuperadmin } from '@/hooks/admin/useIsSuperadmin';
import { CylinderAuditDueBanner } from '@/components/CylinderAuditDueBanner';
import { LocationPicker } from '@/components/layout/LocationPicker';
import { displayName, isPickupItem, type CatalogItem } from '@/types/domain';
import {
  earliestWillCallDate,
  fmtPickupDate,
  validateWillCallDate,
  WILL_CALL_WINDOW,
} from '@/lib/willCall';
import {
  addDays,
  describeSchedule,
  effectiveSchedule,
  fmtCutoffTime,
  fmtScheduleDate,
  isDeliveryDay,
  nextOrderableDeliveryDate,
  ptNow,
} from '@/lib/deliverySchedule';
import type { ShellOutletCtx } from '@/lib/shell-context';
import { useOrderPass } from '@/hooks/useOrderPass';

interface CartLine extends CatalogItem {
  qty: number;
  unitPrice: number;
  isCustom: boolean;
}

/** Small deterministic string hash (djb2) for building a stable, compact
 * idempotency key from a cart fingerprint. Not security-sensitive. */
function stableHash(input: string): string {
  let h = 5381;
  for (let i = 0; i < input.length; i++) {
    h = ((h << 5) + h + input.charCodeAt(i)) >>> 0;
  }
  return h.toString(36);
}

export default function Cart() {
  const navigate = useNavigate();
  const { selectedLocation, selectedLocationId, setSelectedLocationId } =
    useOutletContext<ShellOutletCtx>();
  const { data: items = [] } = useCatalog();
  const { data: pricing } = useCustomerPricing();
  const { data: ctx } = useCustomer();
  const { cart, adjustQty, setQty, remove } = useCart();
  const { mutate: submitOrder, isPending, error: submitError } = useSubmitOrder();
  const { confirmMoneyAction } = useActingAs();

  const [showConfirm, setShowConfirm] = useState(false);
  const [poNumber, setPoNumber] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  // Fulfillment: delivery (default) or will-call pickup at the warehouse.
  // Will-call needs a pickup date — weekday only, no same-day after 12 PM
  // (client mirror of the server rules in _lib/will-call.ts).
  const [fulfillment, setFulfillment] = useState<'delivery' | 'will_call'>('delivery');
  const [pickupDate, setPickupDate] = useState('');
  const pickupCheck =
    fulfillment === 'will_call' ? validateWillCallDate(pickupDate) : ({ ok: true } as const);
  const [dryRunResult, setDryRunResult] = useState<SubmitOrderResult | null>(null);
  // Tank-return flow (runs before the submit modal when the order has gas):
  // 'ask' → do you have empties? · 'select' → pick types + counts ·
  // 'confirm' → "you selected N for pickup — right?"
  const [returnStep, setReturnStep] = useState<null | 'ask' | 'select' | 'confirm'>(null);
  const [returnQtys, setReturnQtys] = useState<Record<string, number>>({});

  const lines = useMemo<CartLine[]>(() => {
    const out: CartLine[] = [];
    for (const [id, qty] of Object.entries(cart)) {
      if (qty <= 0) continue;
      const item = items.find((it) => it.id === id);
      if (!item) continue;
      const { unitPrice, isCustom } = priceFor(item, pricing);
      out.push({ ...item, qty, unitPrice, isCustom });
    }
    return out;
  }, [items, pricing, cart]);

  // Customer pricing hasn't resolved yet — mask money so the list price
  // never flashes before the resolved (contract / price-book) price lands.
  const pricePending = pricingPending(pricing);
  const money = (v: number) => (pricePending ? '—' : formatUsd(v));

  const subtotal = lines.reduce((s, l) => s + l.qty * l.unitPrice, 0);
  const savings = lines.reduce((s, l) => s + l.qty * (l.listPrice - l.unitPrice), 0);
  const count = lines.reduce((s, l) => s + l.qty, 0);

  // Resolve the location id we'll submit against. Prefer the active id from
  // the shell (the picker for master accounts / the auto-selected primary for
  // 1:1 accounts), validated against this account's locations. Fall back to
  // the user's default, then the first location.
  const locationId = useMemo(() => {
    if (!ctx) return null;
    const fromShell = ctx.locations.find((l) => l.id === selectedLocationId);
    return (
      fromShell?.id ??
      ctx.user.defaultLocationId ??
      ctx.locations[0]?.id ??
      null
    );
  }, [ctx, selectedLocationId]);

  // Effective delivery schedule for the submit location (location override
  // wins over the customer default). When one exists, delivery orders get a
  // date input defaulted to the next orderable delivery day; the schedule
  // guides, never blocks — an off-schedule date still submits (ops decides).
  const schedule = useMemo(() => {
    if (!ctx) return null;
    const loc = ctx.locations.find((l) => l.id === locationId) ?? null;
    return effectiveSchedule(loc?.deliverySchedule ?? null, ctx.customer.deliverySchedule);
  }, [ctx, locationId]);
  const [deliveryDate, setDeliveryDate] = useState('');
  useEffect(() => {
    if (fulfillment === 'delivery' && schedule && !deliveryDate) {
      const next = nextOrderableDeliveryDate(schedule);
      if (next) setDeliveryDate(next);
    }
  }, [fulfillment, schedule, deliveryDate]);

  // Hard ordering lock: a superadmin has flipped on_credit_hold on this
  // account. Distinct from (and stronger than) the soft past-due note —
  // this fully blocks submit + preview here, and the server backstops it
  // with a 403 in submit-order. Keep the soft note for owing-but-not-locked
  // customers; this banner only shows when the account is actually locked.
  // A payment clearing that covers the whole past-due amount lets the account
  // place ONE order before it posts (Sky, 2026-10-22). submit-order applies
  // the same rule (fn_order_pass_for) and is what actually decides.
  const { data: orderPass } = useOrderPass(ctx?.customer.id);
  const onHoldWithPass = ctx?.customer.onCreditHold === true && orderPass?.eligible === true;
  const orderingLocked = ctx?.customer.onCreditHold === true && !onHoldWithPass;
  // Staff override (superadmin only): place an order despite the hold. The
  // server re-verifies is_superadmin and audit-logs the override, so this
  // toggle is UI convenience, not the authorization.
  const { isSuperadmin } = useIsSuperadmin();
  const [overrideHold, setOverrideHold] = useState(false);
  const effectiveLocked = orderingLocked && !(isSuperadmin && overrideHold);
  const ready = Boolean(
    ctx &&
      locationId &&
      lines.length > 0 &&
      !effectiveLocked &&
      (fulfillment === 'delivery' || (pickupDate && pickupCheck.ok)),
  );

  // Tank-return flow inputs. The pickup catalog rows (PU* skus) are hidden
  // from the shop; the only way they enter an order is through this flow.
  const pickupItems = useMemo(
    () =>
      items
        .filter((it) => isPickupItem(it) && (it.sku ?? '').toUpperCase().startsWith('PU'))
        .sort((a, b) => displayName(a).localeCompare(displayName(b))),
    [items],
  );
  const hasGasInCart = lines.some((l) => l.category === 'Gas' && !isPickupItem(l));
  const returnCount = Object.values(returnQtys).reduce((s, n) => s + n, 0);

  /** "Review & submit" — when the order carries gas cylinders, ask about
   * empties first; otherwise go straight to the existing submit modal. */
  function startReview() {
    if (!ready) return;
    if (hasGasInCart && pickupItems.length > 0) {
      // Seed the selector from any pickup lines already in the cart (e.g. a
      // reorder from history) so the flow edits rather than duplicates them.
      const seed: Record<string, number> = {};
      for (const p of pickupItems) if (cart[p.id]) seed[p.id] = cart[p.id]!;
      setReturnQtys(seed);
      setReturnStep('ask');
    } else {
      setShowConfirm(true);
    }
  }

  /** Confirmed returns: write them into the cart as $0 pickup lines (they
   * ride the order into Service Fusion like any other line), then continue
   * to the submit modal. */
  function applyReturnsAndContinue() {
    for (const p of pickupItems) setQty(p.id, returnQtys[p.id] ?? 0);
    setReturnStep(null);
    setShowConfirm(true);
  }

  function runDryRun() {
    if (!ctx || !locationId || effectiveLocked) return;
    setDryRunResult(null);
    submitOrder(
      {
        customer_id: ctx.customer.id,
        customer_location_id: locationId,
        lines: lines.map((l) => ({ catalog_item_id: l.id, quantity: l.qty })),
        po_number: poNumber || undefined,
        delivery_notes: deliveryNotes || undefined,
        fulfillment,
        requested_delivery_date:
          fulfillment === 'will_call' ? pickupDate : deliveryDate || undefined,
        dry_run: true,
        override_hold: orderingLocked && isSuperadmin && overrideHold ? true : undefined,
      },
      { onSuccess: (data) => setDryRunResult(data) },
    );
  }

  function runRealSubmit() {
    if (!ctx || !locationId || effectiveLocked) return;
    if (!confirmMoneyAction('Place this order')) return;
    const orderLines = lines.map((l) => ({ catalog_item_id: l.id, quantity: l.qty }));
    // Stable idempotency key: derived from the cart contents (not Date.now())
    // so a re-click or a retry after a partial failure reuses the same key and
    // the backend dedups instead of creating a second Service Fusion job.
    // Changing the cart legitimately yields a new key (a new order).
    const fingerprint = JSON.stringify({
      c: ctx.customer.id,
      loc: locationId,
      lines: [...orderLines].sort((a, b) =>
        a.catalog_item_id.localeCompare(b.catalog_item_id),
      ),
      po: poNumber || '',
      notes: deliveryNotes || '',
      fulfillment,
      pickup: fulfillment === 'will_call' ? pickupDate : '',
      delivery: fulfillment === 'delivery' ? deliveryDate : '',
    });
    submitOrder(
      {
        customer_id: ctx.customer.id,
        customer_location_id: locationId,
        lines: orderLines,
        po_number: poNumber || undefined,
        delivery_notes: deliveryNotes || undefined,
        fulfillment,
        requested_delivery_date:
          fulfillment === 'will_call' ? pickupDate : deliveryDate || undefined,
        idempotency_key: `${ctx.user.id}:${locationId}:${stableHash(fingerprint)}`,
        dry_run: false,
        override_hold: orderingLocked && isSuperadmin && overrideHold ? true : undefined,
      },
      {
        onSuccess: (data) => {
          if (data.order_id) {
            navigate(
              `/confirmation?order_id=${data.order_id}&sf_job_id=${data.sf_job_id ?? ''}`,
            );
          }
        },
      },
    );
  }

  return (
    <div className="h-full overflow-y-auto scrollbar-thin pb-20 md:pb-10">
      <div className="max-w-2xl mx-auto px-4 md:px-8 pt-6">
        <button
          type="button"
          onClick={() => navigate('/order')}
          className="flex items-center gap-1 text-xs text-brix-muted hover:text-brix-ink mb-3 transition-colors duration-200 cursor-pointer"
        >
          <ArrowLeft size={14} /> Back to catalog
        </button>

        <div className="mb-4">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <h1 className="font-display text-3xl font-semibold tracking-tight text-brix-ink">
              Current order
            </h1>
            <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-brix-primary-tint text-brix-primary">
              {count} {count === 1 ? 'item' : 'items'}
            </span>
          </div>
          {/* Master accounts can switch the delivery location here; 1:1
              accounts just see their single delivery address as text. */}
          {ctx && ctx.locations.length > 1 ? (
            <div className="mt-2">
              <LocationPicker
                selectedLocationId={selectedLocationId}
                setSelectedLocationId={setSelectedLocationId}
              />
            </div>
          ) : (
            <p className="text-sm mt-1 text-brix-muted">{selectedLocation}</p>
          )}
        </div>

        {onHoldWithPass && (
          <div className="mb-4 rounded-2xl border border-emerald-300 bg-emerald-50 px-5 py-4">
            <p className="font-display text-base font-bold text-slate-900 leading-tight">
              Your payment is clearing — you can place one order
            </p>
            <p className="text-sm text-slate-700 mt-1 leading-relaxed">
              Your account is on hold, but the payment you made covers the past-due balance. While
              it clears (card: about a day, bank/ACH: 3–5 business days) you can place one order.
              Ordering returns to normal once it posts.
            </p>
          </div>
        )}

        {orderingLocked && (
          <div
            role="alert"
            className="mb-4 rounded-2xl border-2 border-red-300 bg-red-50 shadow-card overflow-hidden"
          >
            <div className="h-1.5 bg-red-600" aria-hidden />
            <div className="px-5 py-4 flex items-start gap-3">
              <div className="w-9 h-9 shrink-0 rounded-full bg-red-600 text-white flex items-center justify-center">
                <Lock size={18} />
              </div>
              <div className="min-w-0">
                <p className="font-display text-base font-bold text-slate-900 leading-tight">
                  Ordering is paused on this account
                </p>
                <p className="text-sm text-slate-700 mt-1 leading-relaxed">
                  This account has an outstanding balance, so new orders are
                  on hold. Please contact billing to resume ordering.
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <a
                    href={SUPPORT_MAILTO_HREF}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-red-300 bg-white text-sm font-semibold text-red-700 hover:bg-red-100 transition-colors cursor-pointer"
                  >
                    <Mail size={14} /> Email billing
                  </a>
                  <a
                    href={SUPPORT_TOLL_FREE_HREF}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-red-300 bg-white text-sm font-semibold text-red-700 hover:bg-red-100 transition-colors cursor-pointer"
                  >
                    <Phone size={14} /> {SUPPORT_PHONE_TOLL_FREE}
                  </a>
                </div>
                {isSuperadmin && (
                  <label className="mt-3 flex cursor-pointer items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5">
                    <input
                      type="checkbox"
                      checked={overrideHold}
                      onChange={(e) => setOverrideHold(e.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-amber-600"
                    />
                    <span className="text-sm text-amber-900">
                      <span className="font-bold">Staff override</span> — place this
                      order despite the hold. Logged to the audit trail under your
                      name.
                    </span>
                  </label>
                )}
              </div>
            </div>
          </div>
        )}

        {lines.length === 0 ? (
          <div className="rounded-2xl border border-brix-border bg-brix-surface px-6 py-12 text-center shadow-card">
            <ShoppingBag size={28} className="mx-auto mb-3 text-brix-muted" />
            <p className="text-sm text-brix-ink font-medium">Your cart is empty</p>
            <p className="text-xs mt-1 text-brix-muted mb-4">
              Add items from the catalog to start an order.
            </p>
            <Button type="button" onClick={() => navigate('/order')}>
              Browse catalog
            </Button>
          </div>
        ) : (
          <>
            <ul className="rounded-2xl border border-brix-border bg-brix-surface shadow-card divide-y divide-brix-border overflow-hidden">
              {lines.map((line) => (
                <li key={line.id} className="px-4 py-3">
                  <div className="flex items-start gap-3">
                    <div
                      className="w-2 h-10 rounded-sm shrink-0 mt-0.5"
                      style={{ background: line.accentColor ?? '#6A7780' }}
                      aria-hidden
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-brix-ink truncate">{line.name}</p>
                      <p className="text-[11px] text-brix-muted">
                        {line.category} · <span className="font-mono">{line.sku}</span>
                      </p>
                      <div className="flex items-baseline gap-1.5 mt-1">
                        <p className="text-sm font-mono font-semibold text-brix-ink">
                          {money(line.unitPrice)}
                        </p>
                        {/* Struck through only when list is HIGHER — see
                            ProductCard. Above list it would claim a saving
                            that isn't there. */}
                        {!pricePending && line.isCustom && line.unitPrice < line.listPrice && (
                          <p className="text-[10px] font-mono line-through text-brix-muted">
                            {formatUsd(line.listPrice)}
                          </p>
                        )}
                      </div>
                    </div>
                    <p className="text-sm font-mono font-semibold whitespace-nowrap text-brix-ink">
                      {money(line.unitPrice * line.qty)}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 mt-2 ml-5">
                    <button
                      type="button"
                      onClick={() => adjustQty(line.id, -1)}
                      aria-label={`Decrease ${line.name}`}
                      className="w-7 h-7 rounded-md border border-brix-primary text-brix-primary flex items-center justify-center hover:bg-brix-primary-tint cursor-pointer transition-colors duration-200"
                    >
                      <Minus size={12} />
                    </button>
                    <input
                      type="number"
                      min={0}
                      value={line.qty}
                      onChange={(e) => setQty(line.id, parseInt(e.target.value || '0', 10))}
                      className="w-12 text-center font-mono font-bold text-sm outline-none border border-brix-border rounded-md py-1 focus-visible:border-brix-primary"
                      aria-label={`${line.name} quantity`}
                    />
                    <button
                      type="button"
                      onClick={() => adjustQty(line.id, 1)}
                      aria-label={`Increase ${line.name}`}
                      className="w-7 h-7 rounded-md bg-brix-primary text-white flex items-center justify-center hover:opacity-90 cursor-pointer transition-colors duration-200"
                    >
                      <Plus size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(line.id)}
                      className="ml-auto text-[11px] text-brix-muted hover:text-brix-warning cursor-pointer transition-colors duration-200"
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            {/* Scheduled customers pick (or accept) a delivery date. Defaults
                to the next orderable day on their route; off-schedule dates
                warn but never block — ops can always accommodate. */}
            {fulfillment === 'delivery' && schedule ? (
              <div className="rounded-2xl border border-brix-border bg-brix-surface shadow-card mt-4 p-4 space-y-2">
                <div className="flex items-start gap-2.5">
                  <CalendarDays size={16} className="text-brix-primary mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-brix-ink">Delivery date</p>
                    <p className="text-[11px] text-brix-muted mt-0.5">
                      Your delivery days: {describeSchedule(schedule)} · order by{' '}
                      {fmtCutoffTime(schedule)} the day before.
                    </p>
                  </div>
                </div>
                <div className="space-y-1">
                  <Input
                    id="delivery-date"
                    type="date"
                    aria-label="Delivery date"
                    value={deliveryDate}
                    min={addDays(ptNow().date, 1)}
                    onChange={(e) => setDeliveryDate(e.target.value)}
                    className="bg-brix-surface"
                  />
                  {deliveryDate && isDeliveryDay(schedule, deliveryDate) ? (
                    <p className="text-[11px] text-emerald-700 flex items-center gap-1">
                      <CheckCircle2 size={11} /> Delivery {fmtScheduleDate(deliveryDate)} —
                      on your schedule
                    </p>
                  ) : deliveryDate ? (
                    <p className="text-[11px] text-amber-700 font-medium">
                      {fmtScheduleDate(deliveryDate)} isn&apos;t one of your scheduled
                      delivery days — we&apos;ll do our best, but it may move to the next
                      scheduled day.
                    </p>
                  ) : (
                    <p className="text-[11px] text-brix-muted">
                      Leave blank and we&apos;ll schedule your next delivery day.
                    </p>
                  )}
                </div>
              </div>
            ) : null}

            {/* Fulfillment: delivery by default; a friendly nudge offers
                will-call pickup (most customers don't know it exists). */}
            {fulfillment === 'delivery' ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 shadow-card mt-4 p-4">
                <div className="flex items-start gap-2.5">
                  <Store size={16} className="text-emerald-700 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-brix-ink">
                      Want to pick up your items and save on delivery fees?
                    </p>
                    <p className="text-[11px] text-brix-muted mt-0.5">
                      Will-call pickup at our warehouse — {WILL_CALL_WINDOW}.
                    </p>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="mt-2 border-emerald-300"
                      onClick={() => {
                        setFulfillment('will_call');
                        if (!pickupDate) setPickupDate(earliestWillCallDate());
                      }}
                    >
                      <Store size={13} /> Switch to Will-Call Pickup
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-emerald-300 bg-emerald-50/70 shadow-card mt-4 p-4 space-y-2.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2.5 min-w-0">
                    <Store size={16} className="text-emerald-700 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-brix-ink">Will-Call Pickup</p>
                      <p className="text-[11px] text-brix-muted mt-0.5">
                        Pick up at our warehouse · {WILL_CALL_WINDOW}. Same-day orders close
                        at 12:00 PM.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="shrink-0 inline-flex items-center gap-1 text-[11px] font-medium text-brix-primary hover:underline cursor-pointer"
                    onClick={() => setFulfillment('delivery')}
                  >
                    <Truck size={12} /> Switch back to delivery
                  </button>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="pickup-date" className="text-[11px]">
                    Pickup date
                  </Label>
                  <Input
                    id="pickup-date"
                    type="date"
                    value={pickupDate}
                    min={earliestWillCallDate()}
                    onChange={(e) => setPickupDate(e.target.value)}
                    className="bg-brix-surface"
                  />
                  {pickupDate && !pickupCheck.ok ? (
                    <p className="text-[11px] text-red-700 font-medium">
                      {(pickupCheck as { ok: false; error: string }).error}
                    </p>
                  ) : pickupDate ? (
                    <p className="text-[11px] text-emerald-700 flex items-center gap-1">
                      <CalendarDays size={11} /> Pickup {fmtPickupDate(pickupDate)} ·{' '}
                      9:30 AM – 3:00 PM
                    </p>
                  ) : (
                    <p className="text-[11px] text-brix-muted">Pick a weekday for pickup.</p>
                  )}
                </div>
              </div>
            )}

            <div className="rounded-2xl border border-brix-border bg-brix-surface shadow-card mt-4 p-5">
              <div className="space-y-1.5 mb-3">
                <Row label="Subtotal" value={money(subtotal)} />
                <Row
                  label={fulfillment === 'will_call' ? 'Pickup' : 'Delivery'}
                  value={fulfillment === 'will_call' ? 'Will-call · free' : 'Included'}
                  muted
                />
                <Row label="Tax" value="Calc. at invoice" muted />
                {!pricePending && savings > 0 && (
                  <Row label="Your savings" value={`-${formatUsd(savings)}`} success />
                )}
              </div>

              <div className="flex items-center justify-between py-3 border-t border-b border-brix-border mb-4">
                <p className="font-display text-base font-semibold text-brix-ink">
                  Estimated total
                </p>
                <p className="font-display text-2xl font-semibold font-mono text-brix-primary">
                  {money(subtotal)}
                </p>
              </div>

              <CylinderAuditDueBanner compact />

              <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg mb-3 bg-brix-primary-tint">
                <AlertCircle size={14} className="text-brix-primary mt-0.5 shrink-0" />
                <p className="text-[11px] leading-relaxed text-brix-primary">
                  <span className="font-semibold">Net 30.</span> Invoice issues after{' '}
                  {fulfillment === 'will_call' ? 'pickup' : 'delivery'}. No payment at order
                  time.
                </p>
              </div>

              <Button
                type="button"
                size="lg"
                className="w-full"
                disabled={!ready}
                onClick={startReview}
              >
                {effectiveLocked ? (
                  <>
                    <Lock size={16} /> Ordering paused
                  </>
                ) : (
                  'Review & submit'
                )}
              </Button>
              {orderingLocked && !effectiveLocked ? (
                <p className="text-[11px] text-amber-700 text-center mt-2 font-medium">
                  Staff override active — this order bypasses the credit hold
                  and is audit-logged.
                </p>
              ) : orderingLocked ? (
                <p className="text-[11px] text-red-700 text-center mt-2 font-medium">
                  This account is on hold for an outstanding balance — contact
                  billing to resume ordering.
                </p>
              ) : !ctx ? (
                <p className="text-[10px] text-brix-muted text-center mt-2">
                  Loading your account…
                </p>
              ) : null}
            </div>
          </>
        )}
      </div>

      {returnStep && (
        <TankReturnModal
          step={returnStep}
          pickupItems={pickupItems}
          qtys={returnQtys}
          returnCount={returnCount}
          onQty={(id, qty) =>
            setReturnQtys((prev) => {
              const next = { ...prev };
              const clamped = Math.max(0, Math.min(99, Math.floor(qty)));
              if (clamped === 0) delete next[id];
              else next[id] = clamped;
              return next;
            })
          }
          onNo={() => {
            // No empties — clear any previously selected returns and go
            // straight to the confirmation / PO-note screen.
            setReturnQtys({});
            for (const p of pickupItems) if (cart[p.id]) setQty(p.id, 0);
            setReturnStep(null);
            setShowConfirm(true);
          }}
          onYes={() => setReturnStep('select')}
          onSelected={() => setReturnStep(returnCount > 0 ? 'confirm' : 'ask')}
          onAdjust={() => setReturnStep('select')}
          onConfirm={applyReturnsAndContinue}
          onClose={() => setReturnStep(null)}
        />
      )}

      {showConfirm && (
        <SubmitModal
          fulfillment={fulfillment}
          pickupDate={pickupDate}
          subtotal={subtotal}
          itemCount={count}
          locationName={selectedLocation}
          lines={lines}
          poNumber={poNumber}
          setPoNumber={setPoNumber}
          deliveryNotes={deliveryNotes}
          setDeliveryNotes={setDeliveryNotes}
          isPending={isPending}
          error={submitError instanceof Error ? submitError.message : null}
          dryRunResult={dryRunResult}
          onClose={() => {
            if (isPending) return;
            setShowConfirm(false);
            setDryRunResult(null);
          }}
          onDryRun={runDryRun}
          onSubmit={runRealSubmit}
        />
      )}
    </div>
  );
}

/**
 * Guided tank-return flow, shown before the submit modal when the order
 * contains gas cylinders:
 *   ask     → "Do you have empty tanks for us to pick up?"  No → submit modal.
 *   select  → per-type steppers for the PU* pickup items.
 *   confirm → "You selected N tank(s) for return & pickup" → yes continues,
 *             no goes back to adjust.
 */
function TankReturnModal(props: {
  step: 'ask' | 'select' | 'confirm';
  pickupItems: CatalogItem[];
  qtys: Record<string, number>;
  returnCount: number;
  onQty: (id: string, qty: number) => void;
  onNo: () => void;
  onYes: () => void;
  onSelected: () => void;
  onAdjust: () => void;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const selected = props.pickupItems.filter((p) => (props.qtys[p.id] ?? 0) > 0);
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={props.onClose}
        className="absolute inset-0 bg-brix-ink/40 backdrop-blur-sm"
      />
      <div className="relative w-full max-w-md max-h-[90vh] overflow-y-auto bg-brix-surface rounded-2xl border border-brix-border shadow-card-hover">
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-3 bg-brix-surface/95 backdrop-blur-md border-b border-brix-border">
          <h2 className="font-display text-lg font-semibold text-brix-ink">
            {props.step === 'ask' && 'Empty tanks to return?'}
            {props.step === 'select' && 'Tanks to return'}
            {props.step === 'confirm' && 'Confirm tank pickup'}
          </h2>
          <button
            type="button"
            onClick={props.onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-md flex items-center justify-center text-brix-muted hover:text-brix-ink hover:bg-brix-surface-alt transition-colors duration-150 cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {props.step === 'ask' && (
            <>
              <p className="text-sm text-brix-ink leading-relaxed">
                You&rsquo;re ordering gas cylinders. Do you have{' '}
                <span className="font-semibold">empty tanks</span> for our driver to
                pick up with this delivery?
              </p>
              <div className="flex flex-col gap-2 pt-1">
                <Button type="button" size="lg" className="w-full" onClick={props.onYes}>
                  Yes — select tanks to return
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  className="w-full"
                  onClick={props.onNo}
                >
                  No — continue to order
                </Button>
              </div>
            </>
          )}

          {props.step === 'select' && (
            <>
              <p className="text-sm text-brix-muted leading-relaxed">
                What tanks do you have to return? Select each type and how many.
              </p>
              <ul className="rounded-xl border border-brix-border divide-y divide-brix-border overflow-hidden">
                {props.pickupItems.map((p) => {
                  const qty = props.qtys[p.id] ?? 0;
                  return (
                    <li key={p.id} className="flex items-center gap-3 px-3 py-2.5 bg-brix-surface">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-brix-ink truncate">
                          {displayName(p).replace(/^Pick up /i, '')}
                        </p>
                        <p className="text-[10px] font-mono text-brix-muted">{p.sku}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => props.onQty(p.id, qty - 1)}
                          aria-label={`Fewer ${displayName(p)}`}
                          className="w-7 h-7 rounded-md border border-brix-primary text-brix-primary flex items-center justify-center hover:bg-brix-primary-tint cursor-pointer transition-colors duration-200 disabled:opacity-40"
                          disabled={qty === 0}
                        >
                          <Minus size={12} />
                        </button>
                        <input
                          type="number"
                          min={0}
                          value={qty}
                          onChange={(e) => props.onQty(p.id, parseInt(e.target.value || '0', 10))}
                          className="w-10 text-center font-mono font-bold text-sm outline-none border border-brix-border rounded-md py-1 focus-visible:border-brix-primary"
                          aria-label={`${displayName(p)} return quantity`}
                        />
                        <button
                          type="button"
                          onClick={() => props.onQty(p.id, qty + 1)}
                          aria-label={`More ${displayName(p)}`}
                          className="w-7 h-7 rounded-md bg-brix-primary text-white flex items-center justify-center hover:opacity-90 cursor-pointer transition-colors duration-200"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <div className="flex flex-col gap-2 pt-1">
                <Button
                  type="button"
                  size="lg"
                  className="w-full"
                  disabled={props.returnCount === 0}
                  onClick={props.onSelected}
                >
                  Continue{props.returnCount > 0 ? ` — ${props.returnCount} tank${props.returnCount === 1 ? '' : 's'}` : ''}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={props.onNo}
                >
                  Actually, no tanks to return
                </Button>
              </div>
            </>
          )}

          {props.step === 'confirm' && (
            <>
              <p className="text-sm text-brix-ink leading-relaxed">
                You have selected{' '}
                <span className="font-semibold">
                  {props.returnCount} tank{props.returnCount === 1 ? '' : 's'}
                </span>{' '}
                for return &amp; pickup:
              </p>
              <ul className="rounded-xl border border-brix-border divide-y divide-brix-border overflow-hidden">
                {selected.map((p) => (
                  <li
                    key={p.id}
                    className="flex items-center justify-between gap-3 px-3 py-2.5 bg-brix-surface"
                  >
                    <p className="text-sm text-brix-ink truncate">
                      {displayName(p).replace(/^Pick up /i, '')}
                    </p>
                    <p className="text-sm font-mono font-bold text-brix-ink shrink-0">
                      × {props.qtys[p.id]}
                    </p>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col gap-2 pt-1">
                <Button type="button" size="lg" className="w-full" onClick={props.onConfirm}>
                  Yes — continue to order
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  className="w-full"
                  onClick={props.onAdjust}
                >
                  No — adjust tanks
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SubmitModal(props: {
  fulfillment: 'delivery' | 'will_call';
  pickupDate: string;
  subtotal: number;
  itemCount: number;
  locationName: string;
  lines: CartLine[];
  poNumber: string;
  setPoNumber: (v: string) => void;
  deliveryNotes: string;
  setDeliveryNotes: (v: string) => void;
  isPending: boolean;
  error: string | null;
  dryRunResult: SubmitOrderResult | null;
  onClose: () => void;
  onDryRun: () => void;
  onSubmit: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={props.onClose}
        className="absolute inset-0 bg-brix-ink/40 backdrop-blur-sm"
      />
      <div className="relative w-full max-w-md max-h-[90vh] overflow-y-auto bg-brix-surface rounded-2xl border border-brix-border shadow-card-hover">
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-3 bg-brix-surface/95 backdrop-blur-md border-b border-brix-border">
          <h2 className="font-display text-lg font-semibold text-brix-ink">Confirm order</h2>
          <button
            type="button"
            onClick={props.onClose}
            aria-label="Close"
            disabled={props.isPending}
            className="w-8 h-8 rounded-md flex items-center justify-center text-brix-muted hover:text-brix-ink hover:bg-brix-surface-alt transition-colors duration-150 cursor-pointer disabled:opacity-50"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-xl border border-brix-border bg-brix-surface-alt p-4">
            <p className="text-[10px] tracking-tight text-brix-muted font-semibold">
              {props.fulfillment === 'will_call' ? 'Will-call pickup for' : 'Delivering to'}
            </p>
            <p className="text-sm font-semibold text-brix-ink mt-0.5">{props.locationName}</p>
            {props.fulfillment === 'will_call' && (
              <p className="text-[11px] text-emerald-700 font-medium mt-1">
                Pickup {fmtPickupDate(props.pickupDate)} · 9:30 AM – 3:00 PM at our warehouse
              </p>
            )}
            <p className="text-xs text-brix-muted mt-2">
              {props.itemCount} {props.itemCount === 1 ? 'item' : 'items'} ·{' '}
              <span className="font-mono font-semibold text-brix-ink">
                {formatUsd(props.subtotal)}
              </span>{' '}
              estimated
            </p>
          </div>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="po">PO # (optional)</Label>
              <Input
                id="po"
                type="text"
                placeholder="Your internal PO reference"
                value={props.poNumber}
                onChange={(e) => props.setPoNumber(e.target.value)}
                maxLength={64}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="notes">
                {props.fulfillment === 'will_call'
                  ? 'Notes for our team (optional)'
                  : 'Notes for the driver (optional)'}
              </Label>
              <textarea
                id="notes"
                placeholder="Gate code, dock door, side entrance, etc."
                value={props.deliveryNotes}
                onChange={(e) => props.setDeliveryNotes(e.target.value)}
                rows={3}
                maxLength={500}
                className="w-full rounded-md border border-brix-border bg-brix-surface px-3 py-2 text-sm placeholder:text-brix-muted/70 focus-visible:outline-none focus-visible:border-brix-primary focus-visible:ring-2 focus-visible:ring-brix-primary/20"
              />
            </div>
          </div>

          {props.error && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-brix-primary-tint">
              <AlertCircle size={14} className="text-brix-warning mt-0.5 shrink-0" />
              <p className="text-[11px] leading-relaxed text-brix-ink">{props.error}</p>
            </div>
          )}

          {props.dryRunResult && (
            <div className="rounded-xl border border-brix-border bg-brix-surface-alt p-4">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 size={14} className="text-brix-success" />
                <p className="text-[11px] font-semibold text-brix-ink">
                  Order preview — what we'll submit
                </p>
              </div>

              <p className="text-[10px] tracking-tight text-brix-muted font-semibold">
                {props.fulfillment === 'will_call' ? 'Will-call pickup for' : 'Delivering to'}
              </p>
              <p className="text-sm font-semibold text-brix-ink mt-0.5 mb-3">
                {props.dryRunResult.location?.display_name ?? props.locationName}
                {props.fulfillment === 'will_call' && (
                  <span className="block text-[11px] font-medium text-emerald-700 mt-0.5">
                    Pickup {fmtPickupDate(props.pickupDate)} · 9:30 AM – 3:00 PM
                  </span>
                )}
              </p>

              <ul className="divide-y divide-brix-border">
                {props.lines.map((l) => (
                  <li key={l.id} className="flex items-baseline justify-between gap-3 py-2">
                    <div className="min-w-0">
                      <p className="text-sm text-brix-ink truncate">{l.name}</p>
                      <p className="text-[11px] text-brix-muted font-mono">
                        {l.qty} × {formatUsd(l.unitPrice)}
                      </p>
                    </div>
                    <p className="text-sm font-mono font-semibold text-brix-ink shrink-0">
                      {formatUsd(l.qty * l.unitPrice)}
                    </p>
                  </li>
                ))}
              </ul>

              {(props.dryRunResult.fee_lines?.length ?? 0) > 0 && (
                <div className="pt-2 mt-1 border-t border-dashed border-brix-border">
                  {props.dryRunResult.fee_lines!.map((f) => {
                    const label =
                      f.kind === 'fuel'
                        ? 'Fuel surcharge'
                        : f.kind === 'hazmat'
                          ? 'Hazmat fee'
                          : f.kind === 'force_majeure'
                            ? 'Force majeure fee'
                            : f.kind === 'crv'
                              ? 'CRV'
                              : f.kind === 'wa_syrup'
                                ? 'WA syrup tax'
                                : f.kind === 'sea_ssbt'
                                  ? 'Seattle beverage tax'
                                  : 'Beverage tax';
                    return (
                      <div
                        key={f.qboItemId}
                        className="flex items-baseline justify-between gap-3 py-1"
                      >
                        <div className="min-w-0">
                          <p className="text-[11px] text-brix-ink truncate">
                            {label} — {f.name}
                          </p>
                          <p className="text-[10px] text-brix-muted font-mono">
                            {f.quantity} × {formatUsd(f.fee)}
                          </p>
                        </div>
                        <p className="text-[11px] font-mono font-semibold text-brix-ink shrink-0">
                          {formatUsd(f.amount)}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="flex items-center justify-between gap-3 pt-3 mt-1 border-t border-brix-border">
                <p className="text-xs font-semibold text-brix-ink">Estimated total</p>
                <p className="text-sm font-mono font-bold text-brix-ink">
                  {formatUsd(props.dryRunResult.totals?.estimated_total ?? props.subtotal)}
                </p>
              </div>

              {props.poNumber && (
                <p className="text-[11px] text-brix-muted mt-2">
                  PO #: <span className="text-brix-ink">{props.poNumber}</span>
                </p>
              )}
              {props.deliveryNotes && (
                <p className="text-[11px] text-brix-muted mt-1">
                  Driver notes: <span className="text-brix-ink">{props.deliveryNotes}</span>
                </p>
              )}

              <p className="text-[10px] text-brix-muted mt-3 pt-3 border-t border-brix-border">
                {props.dryRunResult.customer?.sf_customer_id ? (
                  <span className="text-brix-success">
                    ✓ Account verified — ready to submit
                  </span>
                ) : (
                  <span className="text-brix-warning">
                    Account will be matched by name on submit
                  </span>
                )}
              </p>
            </div>
          )}

          <div className="flex flex-col gap-2 pt-2 border-t border-brix-border">
            <Button
              type="button"
              variant="ghost"
              onClick={props.onDryRun}
              disabled={props.isPending}
              className="w-full"
            >
              {props.isPending ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Working…
                </>
              ) : (
                'Preview order'
              )}
            </Button>
            <Button
              type="button"
              size="lg"
              onClick={props.onSubmit}
              disabled={props.isPending}
              className="w-full"
            >
              {props.isPending ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Submitting…
                </>
              ) : (
                'Submit order'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  muted,
  success,
}: {
  label: string;
  value: string;
  muted?: boolean;
  success?: boolean;
}) {
  const color = muted
    ? 'text-brix-muted'
    : success
      ? 'text-brix-success'
      : 'text-brix-ink';
  return (
    <div className="flex items-center justify-between">
      <p className={cn('text-sm', color)}>{label}</p>
      <p className={cn('text-sm font-mono font-semibold', color)}>{value}</p>
    </div>
  );
}
